// Becoming the account-key holder, restoring it, and deciding which of those this browser
// should offer (D41, docs/07 "The account key").
//
// Only the first browser becomes the holder: when the signed-in user has no recipient list and no
// account key yet. Anywhere else a list already exists (signed by the phone or another
// browser), and this browser waits to be approved as before.
import { messages } from "../i18n";
import { base64ToBytes, bytesToBase64, randomBytes } from "../proto/bytes";
import type { Proto } from "../proto/index";
import {
  type AccountKeyDto,
  fetchAccountKey,
  fetchDevices,
  fetchRecipientList,
  fetchSealingKey,
  putAccountKey,
  putManagedAccountKey,
  type RecipientListDto,
} from "./api";
import {
  ACCOUNT_ALG,
  type AccountKey,
  accountFingerprint,
  generateAccountKey,
  generateManagedAccountKey,
  importAccountKey,
  type NewAccountKey,
} from "./key";
import { createRecoveryFile, openRecoveryFile, parseRecoveryFile, RecoveryError, type RecoveryFile } from "./recovery";
import { publishRecipientList } from "./recipients";
import { loadAccountKey, saveAccountKey } from "./store";

export interface AccountStatus {
  /** The key this browser holds, if any. */
  local?: AccountKey;
  /** The account public key the API stores, if one was set. */
  server?: AccountKeyDto;
  list?: RecipientListDto;
}

export type AccountRole =
  /** The platform holds the key and signs the device list (D59). */
  | "managed"
  /** This browser holds the key the API knows (or will, after the pending PUT). */
  | "holder"
  /** This browser holds a key, but the API has a different one. */
  | "mismatch"
  /** Nobody holds a key yet and no list exists: this browser may become the holder. */
  | "create"
  /** A key exists elsewhere; this browser can restore it from the recovery file. */
  | "restore"
  /** A list exists without an account key here or on the API (the phone signs): wait. */
  | "waiting";

export function accountRole(status: AccountStatus): AccountRole {
  const { local, server, list } = status;
  if (server?.mode === "managed") return "managed";
  if (local) return server && server.public_key !== bytesToBase64(local.publicKey) ? "mismatch" : "holder";
  if (server) return "restore";
  return list ? "waiting" : "create";
}

export async function loadAccountStatus(): Promise<AccountStatus> {
  const [local, server, list] = await Promise.all([loadAccountKey(), fetchAccountKey(), fetchRecipientList()]);
  return { local, server, list };
}

/** A generated key and its recovery file, before anything is stored or published. */
export interface PreparedAccountKey {
  key: NewAccountKey;
  fingerprint: string;
  recoveryFile: RecoveryFile;
}

export async function prepareAccountKey(proto: Proto, passphrase: string): Promise<PreparedAccountKey> {
  const key = await generateAccountKey();
  const fingerprint = accountFingerprint(proto, key.publicKey);
  const recoveryFile = await createRecoveryFile(key.pkcs8, key.publicKey, fingerprint, passphrase);
  return { key, fingerprint, recoveryFile };
}

export interface SetupDeps {
  save: (key: AccountKey) => Promise<void>;
}

const defaultDeps: SetupDeps = { save: saveAccountKey };

/**
 * After the recovery file was offered: keep the key non-extractable, tell the API its
 * public half, and sign the first recipient list with this browser alone in it.
 */
export async function finishAccountKey(
  proto: Proto,
  prepared: PreparedAccountKey,
  deviceId: string,
  deps: SetupDeps = defaultDeps,
): Promise<AccountKey> {
  if (await fetchRecipientList())
    throw new Error(messages().account.errors.listExists);
  const key = await importAccountKey(prepared.key.pkcs8, prepared.key.publicKey);
  prepared.key.pkcs8.fill(0);
  await deps.save(key);
  await putAccountKey(ACCOUNT_ALG, key.publicKey);
  const self = (await fetchDevices()).find((d) => d.id === deviceId);
  if (!self) throw new Error(messages().account.errors.notEnrolled);
  await publishRecipientList(proto, key, [self]);
  return key;
}

/**
 * The "managed" choice on the first device (D59): generate the key, seal its private half
 * to the daemon's sealing key for this user, and hand it over. The browser keeps nothing;
 * the platform signs the first list (this browser and its escrow recipient) itself.
 */
export async function createManagedAccountKey(proto: Proto, deviceId: string): Promise<void> {
  if (await fetchRecipientList())
    throw new Error(messages().account.errors.listExists);
  const self = (await fetchDevices()).find((d) => d.id === deviceId);
  if (!self) throw new Error(messages().account.errors.notEnrolled);
  const sealing = await fetchSealingKey();
  const sealingKey = base64ToBytes(sealing.public_key);
  if (proto.sealingKeyId(sealingKey) !== sealing.key_id) throw new Error(messages().account.errors.sealingMismatch(sealing.key_id));
  const key = await generateManagedAccountKey();
  const ikm = randomBytes(32);
  try {
    const sealed = proto.sealAccountKey(sealingKey, self.user_id, ACCOUNT_ALG, ikm, key.scalar);
    await putManagedAccountKey(ACCOUNT_ALG, key.publicKey, sealed);
  } finally {
    ikm.fill(0);
    key.scalar.fill(0);
  }
}

/** Bring the key back from its recovery file after browser storage was lost. */
export async function restoreAccountKey(
  fileText: string,
  passphrase: string,
  deps: SetupDeps = defaultDeps,
): Promise<AccountKey> {
  const file = parseRecoveryFile(fileText);
  const server = await fetchAccountKey();
  if (server && server.public_key !== file.public_key) {
    throw new RecoveryError(messages().account.errors.differentKey(server.fingerprint));
  }
  const key = await openRecoveryFile(file, passphrase);
  await deps.save(key);
  if (!server) await putAccountKey(ACCOUNT_ALG, key.publicKey);
  return key;
}
